import { BookOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { SessionNoteInput } from "@/components/session/SessionNoteInput";
import { cn } from "@/lib/utils";
import type { VocabularyNote } from "@/hooks/use-vocabulary-notes";

interface VocabularyNoteCardProps {
  note: VocabularyNote;
  onSaveContent: (content: string) => void;
  onDelete: () => void;
  /** Show the word + definition header. Off inside the dictionary (word is already in view). */
  showWord?: boolean;
  /** Deep-link highlight (e.g. from the global sidebar) — ring + anchor scroll. */
  highlighted?: boolean;
}

/**
 * A vocabulary note: the word + definition it belongs to, plus the editable
 * rich-text note body (SessionNoteInput handles edit/delete). Vocabulary notes
 * originate in practice sessions, so there is no create flow here.
 */
export const VocabularyNoteCard = ({
  note,
  onSaveContent,
  onDelete,
  showWord = true,
  highlighted = false,
}: VocabularyNoteCardProps) => {
  const { t } = useTranslation();

  return (
    <Card
      id={`note-v-${note.id}`}
      className={cn(
        "rounded-2xl border border-stone-200/60 dark:border-stone-700/60 bg-card transition-all duration-300 hover:shadow-md scroll-mt-24",
        highlighted && "ring-2 ring-primary border-primary/50 shadow-md"
      )}
    >
      <CardContent className="p-4 space-y-3">
        {showWord && (
          <div className="flex items-start gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-primary/5 flex items-center justify-center shrink-0">
              <BookOpen className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-base font-semibold truncate" title={note.word ?? undefined}>
                  {note.word ?? t("pages.vocabularyNotes.unknownWord", "Unknown word")}
                </p>
                {note.ipa && (
                  <span className="text-xs text-muted-foreground/60 font-mono shrink-0">{note.ipa}</span>
                )}
                {note.type && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded-md bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 font-medium shrink-0">
                    {note.type}
                  </span>
                )}
              </div>
              {note.definition && (
                <p className="text-xs text-muted-foreground line-clamp-2 break-words">
                  {note.definition}
                </p>
              )}
            </div>
          </div>
        )}
        <SessionNoteInput value={note.content} onSave={onSaveContent} onDelete={onDelete} />
      </CardContent>
    </Card>
  );
};
